// EL SWITCH DE PASTILLAS de la home y de la caja del Agente —
// ADR-2026-09-21-los-switches-de-la-home-se-deslizan.
//
// Vive acá, en `components/`, y no adentro del hero, porque lo usan los dos switches del
// hero (Agente / Herramientas y, adentro de Herramientas, Imágenes / Videos) y el de
// Imagen / Video de la caja del Agente (ADR-2026-09-21-el-switch-de-imagen-o-video), que
// está en `chat-atlas`. Importarlo del hero habría cerrado un ciclo
// `chat-atlas → pages/v2 → chat-atlas` de los que rompen el HMR (`sinCiclos.test.ts`).
//
// Presentacional puro: el valor y el `onCambiar` son de quien lo monta.

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * UN SWITCH DE PASTILLAS, con la pastilla que SE DESLIZA — ADR-2026-09-21-los-switches-de-la-home-se-deslizan.
 *
 * Los dos switches del hero (Agente / Herramientas y, adentro de Herramientas, Imágenes /
 * Videos) pintaban el fondo de la opción elegida y se lo sacaban a la otra: el cambio era
 * instantáneo y el ojo no tenía cómo saber que las dos son el MISMO control con dos
 * posiciones, en vez de dos botones sueltos. Acá la pastilla es un elemento solo, detrás
 * de los botones, que viaja de una a la otra.
 *
 * ── POR QUÉ SE MIDE Y NO ES "50% Y TRANSLATEX(100%)" ──────────────────────
 * Las opciones NO miden lo mismo ("Agente" contra "Herramientas", "Imágenes" contra
 * "Videos"), porque son pastillas al ancho de su texto y no dos mitades. Así que la
 * pastilla toma la posición y el ancho REALES del botón activo, medidos del DOM, y anima
 * los dos. Es también lo que hace que siga andando si mañana cambia una etiqueta.
 *
 * Se vuelve a medir con un `ResizeObserver`: el ancho de las etiquetas cambia cuando
 * termina de cargar la tipografía y cuando cambia el viewport, y sin eso la pastilla
 * queda corrida respecto del texto.
 *
 * La PRIMERA colocación no se anima (`conTransicion`): sin eso, al abrir la home la
 * pastilla se ve viajando desde el borde izquierdo hasta su lugar, que es una animación
 * que nadie pidió. Y con `prefers-reduced-motion` no viaja nunca: aparece donde va.
 */
export interface OpcionDeSwitch<T extends string> {
  id: T;
  label: string;
  icono: ReactNode;
}

/** Los dos looks que el hero usa. `claro`: pastilla blanca sobre una pista gris (el
 *  switch de arriba). `azul`: pastilla en azul clarito y sin pista, sobre el panel blanco
 *  de Herramientas, donde una pista gris sería casi el mismo valor que el fondo. */
export type TemaDeSwitch = 'claro' | 'azul' | 'composer';

const TEMAS_DE_SWITCH: Record<
  TemaDeSwitch,
  { pista: string; pastilla: string; hueco: string; boton: string; activo: string; inactivo: string }
> = {
  claro: {
    pista: 'rounded-full bg-black/[0.05] p-1',
    pastilla: 'bg-white shadow-[0_1px_4px_rgba(16,24,45,0.12)]',
    hueco: 'inset-y-1',
    boton: 'px-4 py-2 text-[13.5px]',
    activo: 'text-brand-black',
    inactivo: 'text-black/50 hover:text-black/70',
  },
  azul: {
    pista: '',
    pastilla: 'bg-[rgb(var(--brand-primary-rgb)_/_0.095)]',
    hueco: 'inset-y-0',
    boton: 'px-4 py-1.5 text-[13px]',
    activo: 'text-brand-primary',
    inactivo: 'text-black/45 hover:text-black/70',
  },
  // El de la fila de pastillas de la caja: más chico que los del hero —comparte renglón
  // con las pastillas de Formato / Calidad / Duración, que miden 30px— y con la pastilla
  // blanca sobre pista gris, que es lo que lo distingue de las pastillas de al lado (esas
  // son botones sueltos; esto es UN control con dos posiciones).
  composer: {
    pista: 'rounded-full bg-black/[0.05] p-0.5',
    pastilla: 'bg-white shadow-[0_1px_3px_rgba(16,24,45,0.14)]',
    hueco: 'inset-y-0.5',
    boton: 'px-2.5 py-1 text-[12.5px]',
    activo: 'text-brand-primary',
    inactivo: 'text-black/50 hover:text-black/70',
  },
};

export function SwitchDePastillas<T extends string>({
  opciones,
  valor,
  onCambiar,
  tema,
}: {
  opciones: OpcionDeSwitch<T>[];
  valor: T;
  onCambiar: (id: T) => void;
  tema: TemaDeSwitch;
}) {
  const t = TEMAS_DE_SWITCH[tema];
  const botones = useRef(new Map<string, HTMLButtonElement>());
  const [pastilla, setPastilla] = useState<{ left: number; width: number } | null>(null);
  const [conTransicion, setConTransicion] = useState(false);
  const cantidad = opciones.length;

  useLayoutEffect(() => {
    const medir = () => {
      const el = botones.current.get(valor);
      if (!el) return;
      const left = el.offsetLeft;
      const width = el.offsetWidth;
      // Se compara antes de setear: el efecto depende de `opciones`, que es un array
      // nuevo en cada render, y un objeto nuevo con los mismos números lo volvería a
      // disparar para siempre.
      setPastilla((p) => (p && p.left === left && p.width === width ? p : { left, width }));
    };
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    botones.current.forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, [valor, cantidad]);

  // Un frame después de la primera medición se habilita la transición: la colocación
  // inicial tiene que ser instantánea.
  useEffect(() => {
    if (!pastilla || conTransicion) return;
    const id = requestAnimationFrame(() => setConTransicion(true));
    return () => cancelAnimationFrame(id);
  }, [pastilla, conTransicion]);

  return (
    <div className={`relative inline-flex items-center gap-1 ${t.pista}`}>
      {/* La pastilla. Va DETRÁS de los botones (ellos son `relative`), así el texto no
          se le desvanece encima al pasar. La curva es la misma con la que Cumbre vuela
          en el arranque del chat: sale rápido y frena suave, que es lo que hace que el
          viaje se lea como un solo gesto y no como un desplazamiento. */}
      {pastilla && (
        <span
          aria-hidden="true"
          className={`absolute left-0 rounded-full ${t.hueco} ${t.pastilla} ${
            conTransicion
              ? 'transition-[transform,width] duration-[280ms] motion-reduce:transition-none'
              : ''
          }`}
          style={{
            width: pastilla.width,
            transform: `translateX(${pastilla.left}px)`,
            transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
      )}
      {opciones.map(({ id, label, icono }) => (
        <button
          key={id}
          ref={(el) => {
            if (el) botones.current.set(id, el);
            else botones.current.delete(id);
          }}
          type="button"
          onClick={() => onCambiar(id)}
          aria-pressed={valor === id}
          className={`relative inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors ${
            t.boton
          } ${valor === id ? t.activo : t.inactivo}`}
        >
          {icono}
          {label}
        </button>
      ))}
    </div>
  );
}
